import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { formatName } from "app/utils/formatter/nameFormatter";
import { formatDate } from "app/utils/formatter/dateTimeFormatter";
import { actions as ProcessEventsActions } from "../../../../Ducks/ProcessEvents.duck";
import { HasAccessTo, SearchInput } from "app/components";
import { CONNECTION } from "app/common/Authorization/entities";
import { DOWNLOAD } from "app/common/Authorization/permissions";
import { IntlMessage } from "../../../../../../components/Intl";
import { EVENT_TYPE_MAPPER } from "app/types";
import { GridItemStyled, GridStyled } from "./ProcessEventsDialog.styles";
import ProcessEventsTableSkeleton from "./ProcessEventsTableSkeleton";
const ProcessEventsDialog = ({ open, connectionId, batchId, batchMongoId, handleClose }) => {
  const dispatch = useDispatch();
  const { items, page, pageSize, totalCount, loading, error } = useSelector((state) => state.ProcessEventsReducer);
  const [selectedEvents, setSelectedEvents] = React.useState([]);
  useEffect(() => {
    dispatch(ProcessEventsActions.clearEvents());
    dispatch(ProcessEventsActions.setConnectionId(connectionId));
    dispatch(ProcessEventsActions.setIds(batchId, batchMongoId));
    dispatch(ProcessEventsActions.requestData());
  }, [connectionId, batchId, batchMongoId, dispatch]);
  const handlePageChange = useCallback(
    (event, newPage) => {
      dispatch(ProcessEventsActions.changePage(newPage + 1));
    },
    [dispatch]
  );
  const handleRowsPerPageChange = useCallback(
    (event) => {
      dispatch(ProcessEventsActions.setPageSize(event.target.value));
    },
    [dispatch]
  );
  const downloadEvents = useCallback(() => {
    dispatch(ProcessEventsActions.downloadEvents(selectedEvents.length > 0 ? selectedEvents : null));
  }, [dispatch, selectedEvents]);
  const disabledIfNoAccessToConnectionDownload = !HasAccessTo(CONNECTION, DOWNLOAD);
  const formatEventType = React.useCallback((eventType) => {
    return /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.type." + EVENT_TYPE_MAPPER[eventType] });
  }, []);
  const handleEventSearch = useCallback(
    (event) => {
      dispatch(ProcessEventsActions.search(event.target.value));
    },
    [dispatch]
  );
  const handleEventTypeChange = useCallback(
    (event) => {
      dispatch(ProcessEventsActions.applyFilter("eventType", event.target.value));
    },
    [dispatch]
  );
  const handleArchivedChange = useCallback(
    (event) => {
      dispatch(ProcessEventsActions.applyFilter("archived", event.target.value));
    },
    [dispatch]
  );
  const handleToggleEvent = useCallback(
    (eventMongoId) => {
      const alreadyExists = selectedEvents.find((item) => item === eventMongoId);
      setSelectedEvents(
        alreadyExists ? selectedEvents.filter((item) => item !== eventMongoId) : [...selectedEvents, eventMongoId]
      );
    },
    [selectedEvents]
  );
  const areAllSelected = React.useMemo(() => {
    if (items.length === 0) return false;
    return items.every((event) => selectedEvents.includes(event.mongoId));
  }, [items, selectedEvents]);
  const handleSelectAll = useCallback(() => {
    if (areAllSelected) {
      const currentPageIds = items.map((event) => event.mongoId);
      setSelectedEvents(selectedEvents.filter((id) => !currentPageIds.includes(id)));
    } else {
      const currentPageIds = items.map((event) => event.mongoId);
      const newSelections = [...selectedEvents];
      currentPageIds.forEach((id) => {
        if (!newSelections.includes(id)) {
          newSelections.push(id);
        }
      });
      setSelectedEvents(newSelections);
    }
  }, [areAllSelected, items, selectedEvents]);
  const eventTypes = React.useMemo(() => {
    return Object.keys(EVENT_TYPE_MAPPER).map((key) => ({
      value: key,
      label: EVENT_TYPE_MAPPER[key]
    }));
  }, []);
  return !error && /* @__PURE__ */ React.createElement(Dialog, { open, onClose: () => handleClose(), fullWidth: true, maxWidth: "md", "aria-labelledby": "process-events-dialog" }, /* @__PURE__ */ React.createElement(DialogTitle, { id: "process-events-dialog" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "connection.processes.events.dialogTitle" })), /* @__PURE__ */ React.createElement(DialogContent, null, /* @__PURE__ */ React.createElement(GridStyled, { container: true, spacing: 3 }, /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 3 }, /* @__PURE__ */ React.createElement(Select, { onChange: handleEventTypeChange, defaultValue: "''", fullWidth: true }, /* @__PURE__ */ React.createElement(MenuItem, { value: "''" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "all" })), eventTypes.map((type) => /* @__PURE__ */ React.createElement(MenuItem, { key: type.value, value: type.value }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.type." + type.label }))))), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 3 }, /* @__PURE__ */ React.createElement(Select, { onChange: handleArchivedChange, defaultValue: "''", fullWidth: true }, /* @__PURE__ */ React.createElement(MenuItem, { value: "''" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "all" })), /* @__PURE__ */ React.createElement(MenuItem, { value: "true" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "yes" })), /* @__PURE__ */ React.createElement(MenuItem, { value: "false" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "no" })))), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(SearchInput, { onChange: handleEventSearch, initialSearchQuery: null }))), /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement(TableHead, null, /* @__PURE__ */ React.createElement(TableRow, null, /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(
    Checkbox,
    {
      color: "primary",
      checked: areAllSelected,
      indeterminate: selectedEvents.length > 0 && !areAllSelected,
      onChange: handleSelectAll
    }
  )), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.type" })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.employee" })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.archived" })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.createdAt" })))), /* @__PURE__ */ React.createElement(TableBody, null, loading ? /* @__PURE__ */ React.createElement(ProcessEventsTableSkeleton, { rows: 10, columns: 5 }) : items.map((event) => /* @__PURE__ */ React.createElement(TableRow, { hover: true, key: event.mongoId }, /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(
    Checkbox,
    {
      color: "primary",
      checked: selectedEvents.includes(event.mongoId),
      onChange: () => handleToggleEvent(event.mongoId)
    }
  )), /* @__PURE__ */ React.createElement(TableCell, null, formatEventType(event.eventType)), /* @__PURE__ */ React.createElement(TableCell, null, formatName(event.hrEmployee.personName)), /* @__PURE__ */ React.createElement(TableCell, null, event.archived ? /* @__PURE__ */ React.createElement(FormattedMessage, { id: "yes" }) : /* @__PURE__ */ React.createElement(FormattedMessage, { id: "no" })), /* @__PURE__ */ React.createElement(TableCell, null, formatDate(event.createdAt))))))), /* @__PURE__ */ React.createElement(DialogActions, null, /* @__PURE__ */ React.createElement(
    TablePagination,
    {
      component: "div",
      count: totalCount,
      onPageChange: handlePageChange,
      onRowsPerPageChange: handleRowsPerPageChange,
      page: page - 1,
      rowsPerPage: pageSize,
      rowsPerPageOptions: [100, 500, 1e3, 1e4]
    }
  ), /* @__PURE__ */ React.createElement(Button, { onClick: downloadEvents, disabled: disabledIfNoAccessToConnectionDownload }, selectedEvents.length > 0 ? /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.download" }) : /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.downloadAll" })), /* @__PURE__ */ React.createElement(Button, { onClick: () => handleClose(), color: "secondary" }, /* @__PURE__ */ React.createElement(IntlMessage, { value: "close" }))));
};
ProcessEventsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  batchId: PropTypes.string.isRequired,
  batchMongoId: PropTypes.string.isRequired,
  connectionId: PropTypes.string.isRequired
};
export default ProcessEventsDialog;
